import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { searchIcon } from "../../../assets";
import ReportsService from "../../../services/ReportsService";
import { componentKey } from "../ReportsSlice";
import { getUsageReport } from "../ReportsSaga";
import {
  CSV_HEADERS_USAGE,
  ROWS_PER_PAGE_OPTIONS,
  USAGE_TYPE_OPTIONS,
  toApiStartDate,
  toApiEndDate,
} from "../ReportConstants";
import DownloadCsvButton from "../components/DownloadCsvButton";

type UsageReportTabProps = { facilityId: string };

const UsageReportTab = ({ facilityId }: UsageReportTabProps) => {
  const dispatch = useDispatch();
  const { usageReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [searchedValue, setSearchedValue] = useState("");
  const [type, setType] = useState("");
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const rows: Array<Record<string, any>> = usageReport?.data || [];

  useEffect(() => {
    dispatch(
      getUsageReport({
        pageNumber,
        limit: rowPerPage,
        facilityId: facilityId || undefined,
        searchValue: searchedValue?.trim() || undefined,
        type: type || undefined,
        startDate: toApiStartDate(startDate),
        endDate: toApiEndDate(endDate),
      })
    );
  }, [
    facilityId,
    pageNumber,
    rowPerPage,
    searchedValue,
    type,
    startDate,
    endDate,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const isAnyFilterApplied = Boolean(
    searchedValue || type || startDate || endDate
  );

  const handleClearFilters = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setSearchedValue("");
    setType("");
    setStartDate(null);
    setEndDate(null);
    setPageNumber(1);
  };

  const columns = [
    {
      field: "itemName",
      label: <div>Item / Medication</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.itemName || "-"}</p>
      ),
    },
    {
      field: "type",
      label: <div>Type</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.type || "-"}</p>
      ),
    },
    {
      field: "facilityName",
      label: <div>Location</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.facilityName || "-"}</p>
      ),
    },
    {
      field: "totalQuantity",
      label: <div>Total Quantity Issued</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-weight-500">
          {row?.totalQuantity ?? 0}
        </p>
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getUsageReport({
      limit: "All",
      facilityId: facilityId || undefined,
      searchValue: searchedValue?.trim() || undefined,
      type: type || undefined,
      startDate: toApiStartDate(startDate),
      endDate: toApiEndDate(endDate),
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => ({
      itemName: row?.itemName,
      type: row?.type,
      facilityName: row?.facilityName,
      totalQuantity: row?.totalQuantity,
    }));
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <SearchSelectDropdown
          freeSolo={true}
          searchedValue={searchedValue}
          onSearchValueChangeCb={handleSearchChange}
          size="small"
          label="Search by Item / Medication Name"
          fullWidth={"280px"}
          startIcon={
            <img alt="search-icon" src={searchIcon} width={"18px"} height={"18px"} />
          }
        />
        <div style={{ minWidth: "170px" }}>
          <SelectDropdown
            label="Type"
            value={type}
            options={USAGE_TYPE_OPTIONS}
            onChangeCb={(e) => {
              setPageNumber(1);
              setType(e.target.value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="From Date"
            value={startDate}
            maxDate={endDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setStartDate(value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="To Date"
            value={endDate}
            minDate={startDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setEndDate(value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_USAGE}
          filename="usage-report.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No usage records found"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: usageReport?.totalPages,
          totalCount: usageReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />
    </div>
  );
};

export default UsageReportTab;
